import React, { useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import { useParams } from "react-router";
import LoadingPageIndicator from "../../../../components/LoadingPageIndicator";
import {
  Admin,
  Organization,
  Patient,
  PaymentMethod,
} from "../../../../models";
import {
  minValue,
  required,
  email as validEmail,
} from "../../../form/utils/validations";
import Centered from "../../../../components/Centered";
import Paper from "@mui/material/Paper";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import ListItemIcon from "@mui/material/ListItemIcon";
import FaceIcon from "@mui/icons-material/Face";
import BusinessIcon from "@mui/icons-material/Business";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import AddIcon from "@mui/icons-material/Add";
import CreditCard from "@mui/icons-material/CreditCard";
import Collapse from "@mui/material/Collapse";

import api, { ViewRequest } from "../../../../api/medAppApi";
import { useDispatch } from "react-redux";
import FormBuilder, { IMedAppFormField } from "../../../form";
import Button from "@mui/material/Button";
import { v4 as uuid } from "uuid";

import { putMessage } from "../../../app/store/actions";
import ListItemButton from "@mui/material/ListItemButton";
import LinearProgress from "@mui/material/LinearProgress";
import { from } from "rxjs";
import { reduxForm } from "redux-form";
import { titleCase } from "../../../../helpers";


const MAX_PAYMENT_METHODS = 8;

const OrganizationMemberCreateForm: React.FC<any> = ({
  mFields,
  name,
  handleSubmit,
  onSubmit,
}) => {
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmitCallback = (value: any) => {
    onSubmit(value);
  };
  return (
    <List component="div" disablePadding>
      <ListItem>
        <form
          style={{ width: "100%" }}
          ref={formRef}
          onSubmit={handleSubmit(handleSubmitCallback)}
        >
          <FormBuilder fields={mFields} name={name} />
          <Button type="submit" variant="contained">
            Continue
          </Button>
        </form>
      </ListItem>
    </List>
  );
};

const OrganizationCreatMemberReduxForm = reduxForm<any, any>({
  form: "organization-create-member",
})(OrganizationMemberCreateForm);

const OrganizationCreatePaymentMethodReduxForm = reduxForm<any, any>({
  form: "organization-create-payment-method",
})(OrganizationMemberCreateForm);

const OrganizationView: React.FC<{
  organization: Organization;
  members: number;
  admins: Admin[];
}> = ({ organization, members, admins }) => {
  const dispatch = useDispatch();
  const [showAdd, setShowAdd] = useState(false);
  const [showPaymentMethods, setShowPaymentMethods] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  const [addingPaymentMethods, setAddingPaymentMethods] = useState(false);
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    if (!showPaymentMethods) {
      return;
    }
    setLoadingPaymentMethods(true);
    const req$ = from(
      api.getModelList<PaymentMethod>({
        modelName: "paymentmethod",
        returnType: "list",
        crieteria: {
          organization: organization.id,
        },
        limit: 20,
      })
    );
    req$.subscribe({
      next: (res) => {
        if (res.code === 200 || res.code === 201) {
          const ans = res!.data!.data!.payload;
          setPaymentMethods(ans);
        }
      },
      complete: () => {
        setLoadingPaymentMethods(false);
      },
    });
  }, [organization, showPaymentMethods]);
  const fields: IMedAppFormField[] = [
    {
      name: "email",
      label: "Email Address",
      type: "email",
      validate: [validEmail, required],
      placeholder: "Email of patient to invite",
    },
    {
      variant: "select",
      inputExtras: ["member", "manager"].map((i) => ({
        value: i,
        label: titleCase(i),
      })),
      name: "membeType",
      label: "Membership Type",
      validate: [required],
      type: "text",
    },
  ];

  const fields2: IMedAppFormField[] = [
    {
      name: "type",
      label: "Type of payment method",
      variant: "select",
      inputExtras: ["Card", "Wallet", "Other", "Invoice"].map((i) => ({
        value: i,
        label: i,
      })),
      validate: [required],
      type: "text",
    },
    {
      name: "credits",
      label: "Credits",
      type: "number",
      validate: [minValue(20)],
      placeholder: "20",
    },
  ];
  const addUser = (value: any) => {
    if (adding) {
      return;
    }

    setAdding(true);
    const resp$ = from(
      api.inviteMember(
        {},
        {},
        {
          ...value,
          sendEmail: true,
        },
        [organization.id + ""]
      )
    );

    resp$.subscribe({
      next: (res) => {
        if (res.code === 200 || res.code === 201) {
          const patient: Patient = res.data!.data.invite.patient as Patient;
          dispatch(
            putMessage({
              type: "success",
              message:
                "Invite sent successfully to " +
                titleCase(`${patient.FirstName}_${patient.LastName}`),
            })
          );
          console.log(res.data?.data.invite);
        }
      },

      complete() {
        setAdding(false);
      },
    });
  };
  const addPaymentMethod = (value: any) => {
    if (addingPaymentMethods) {
      return;
    }
    if(paymentMethods.length === MAX_PAYMENT_METHODS) {
      dispatch(putMessage({
        type: 'error',
        message: `Maximum number of payment methods is ${MAX_PAYMENT_METHODS}`,
      }))
    }

    setAddingPaymentMethods(true);
    const resp$ = from(
      api.createModel<PaymentMethod>({
        modelName: "paymentmethod",
        payload: {
          ...value,
          reference: uuid(),
          organization: organization.id,
        },
      })
    );

    resp$.subscribe({
      next: (res) => {
        if (res.code === 200 || res.code === 201) {
          const ans = res.data!.data!.payload as PaymentMethod;
          setPaymentMethods((p) => p.concat([ans]));
        }
      },

      complete() {
        setAdding(false);
      },
    });
  };
  const handleClick = (drop = "add") => {
    if (drop === "add") setShowAdd(!showAdd);
    else if (drop === "paymentMethod") {
      setShowPaymentMethods(!showPaymentMethods);
    }
  };

  return (
    <Centered>
      <Paper
        style={{
          padding: "1em",
          borderRadius: "14px",
          border: "none",
          width: "80%",

          maxWidth: "450px",
        }}
        elevation={2}
      >
        {adding && <LinearProgress />}
        <List sx={{ width: "100%", bgcolor: "background.paper" }}>
          <ListItem>
            <ListItemIcon>
              <BusinessIcon />
            </ListItemIcon>

            <ListItemText
              primary="Organization Name"
              secondary={organization.organizationName}
            />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <AssignmentIndIcon />
            </ListItemIcon>

            <ListItemText primary="Members" secondary={members + " Members"} />
          </ListItem>

          <ListItem>
            <ListItemIcon>
              <FaceIcon />
            </ListItemIcon>

            <ListItemText
              primary="Admins"
              secondary={admins.length + " Admins"}
            />
          </ListItem>

          <ListItemButton onClick={() => handleClick()}>
            <ListItemIcon>
              <AddIcon />
            </ListItemIcon>
            <ListItemText primary="Add Member" />
          </ListItemButton>
          <ListItemButton onClick={() => handleClick("paymentMethod")}>
            <ListItemIcon>
              <CreditCard />
            </ListItemIcon>
            <ListItemText primary={(showPaymentMethods ? 'Hide ' : 'View ')  + "Payment method"} />
          </ListItemButton>
          <Collapse in={showAdd} timeout="auto" unmountOnExit>
            <OrganizationCreatMemberReduxForm
              mFields={fields}
              namel="kosy-2-org-member"
              onSubmit={addUser}
            />
          </Collapse>

          <Collapse in={showPaymentMethods} timeout="auto">
            {loadingPaymentMethods && <LinearProgress />}
            <List>
              {paymentMethods.map((p) => (
                <ListItemButton key={p.reference}>
                  <ListItemText primary="Type" secondary={p.type} />
                  <ListItemText primary="Credits" secondary={p.credits} />
                </ListItemButton>
              ))}
            </List>
            <OrganizationCreatePaymentMethodReduxForm
              namel="kosy-organization-create-payment-method"
              mFields={fields2}
              onSubmit={addPaymentMethod}
            />
          </Collapse>
        </List>
      </Paper>
    </Centered>
  );
};

const OrganizationViewPage: React.FC<{
  load?: boolean;
}> = ({ load: shouldLoad }) => {
  const dispatch = useDispatch();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars

  const [isLoading, setIsLoading] = useState(true);
  const [organization, setOrganization] = useState<Organization | undefined>();
  const [members, setMembers] = useState(0);
  let { id } = useParams<{ id: string }>();

  const check = async (modelId: number) => {
    setIsLoading(true);
    let _organization;
    try {
      const criteria: ViewRequest = {
        modelName: "organization",
        modelId,
      };
      let ans = await api.viewModel<Organization>(criteria);
      console.log(ans);

      if (ans.code === 200) {
        let { payload } = ans.data!.data;
        _organization = payload;

        let ans2 = await api.getMembersCount({}, {}, undefined, [
          payload!.id + "",
        ]);
        console.log(ans2);
        setMembers(ans2.data?.data.members || 0);
      } else {
        console.log(ans);
        throw new Error("Cannot Load data");
      }
    } catch (err) {
      console.log(err);
      dispatch(
        putMessage({
          type: "error",
          message: "Failed to load data",
        })
      );
    }
    setIsLoading(false);
    setOrganization(_organization);
  };

  useEffect(() => {
    if (shouldLoad) {
      let modelId = parseInt(id!);
      if (isNaN(modelId)) {
        console.error("No valid id found");
        dispatch(
          putMessage({
            type: "error",
            message: "Failed to load data",
          })
        );
        return;
      }
      check(modelId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldLoad, id]);
  return (
    <Box
      sx={{
        width: {
          md: "80vw",
          sm: "100%",
        },
        maxWidth: "800px",
        minWidth: "250px",
        pl: 2,
      }}
    >
      {organization && (
        <OrganizationView
          organization={organization}
          members={members}
          admins={[]}
        />
      )}
      {isLoading && <LoadingPageIndicator />}
    </Box>
  );
};

export default OrganizationViewPage;
